import { APP_VERSION } from '@/config/appVersion';
import type { AppVersionPolicy } from '@/api/appVersionApi';

const BUILD_NUMBER_RE = /\+build\.(\d+)\.sha\./;

/** Extracts the integer build number from a `x.y.z+build.N.sha.<hex>` identity, or `null`. */
export const parseBuildNumber = (version: string): number | null => {
  const match = BUILD_NUMBER_RE.exec(version);

  if (!match) {
    return null;
  }

  const value = Number(match[1]);
  return Number.isInteger(value) ? value : null;
};

/** Build number of the running client, or `null` when its identity is malformed. */
export const currentBuildNumber = (): number | null => parseBuildNumber(APP_VERSION);

/** Human-friendly semver portion (`0.1.0`) without the `+build.N.sha.<hex>` suffix. */
export const toDisplayVersion = (version: string): string => version.split('+')[0] ?? version;

/**
 * Informational only: `true` when the backend advertises a newer build than the
 * running client. Per the Phase C rule this drives a *non-blocking* "update
 * available" banner — it is NEVER used to force an update. Only a 426 from the
 * middleware (handled in C.5) blocks the app.
 */
export const isUpdateAvailable = (policy: AppVersionPolicy | null | undefined): boolean => {
  if (!policy) {
    return false;
  }

  const current = currentBuildNumber();
  return current !== null && policy.latestBuildNumber > current;
};
